import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, User } from 'lucide-react';
import { getRequest } from '../apis/ApiInstence';

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const assetBaseUrl = useMemo(() => {
    let base = process.env.REACT_APP_BACKEND || '';
    base = base.replace('/khajuraho/', '/');
    if (!base.endsWith('/')) {
      base = `${base}/`;
    }
    return base;
  }, []);

  useEffect(() => {
    const getBlogs = async () => {
      setLoading(true);
      try {
        const endpointCandidates = [
          'blogs?page=1&limit=100',
          'blog?page=1&limit=100',
        ];

        let rows = [];
        for (const endpoint of endpointCandidates) {
          try {
            const response = await getRequest(endpoint);
            rows = Array.isArray(response?.data?.data) ? response.data.data : (Array.isArray(response?.data) ? response.data : []);
            if (rows.length > 0) break;
          } catch (error) {
            console.warn(`Blog fetch failed for endpoint: ${endpoint}`, error);
          }
        }

        const mappedBlogs = rows.map((blog) => {
          let parsedImages = [];
          if (Array.isArray(blog.images)) {
            parsedImages = blog.images;
          } else if (typeof blog.images === 'string' && blog.images.trim().length > 0) {
            try {
              const maybeArray = JSON.parse(blog.images);
              parsedImages = Array.isArray(maybeArray) ? maybeArray : [blog.images];
            } catch (e) {
              parsedImages = [blog.images];
            }
          }

          const firstImage = parsedImages.length > 0 ? parsedImages[0] : '';
          const imageUrl = firstImage
            ? (firstImage.startsWith('http') ? firstImage : `${assetBaseUrl}${String(firstImage).replace(/^\/+/, '')}`)
            : '';

          const plainContent = String(blog.content || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

          return {
            id: blog.id,
            img: imageUrl,
            category: blog?.category?.name || 'General',
            title: blog.heading || 'Untitled',
            desc: plainContent,
            author: blog.author || 'GRAAHO Team',
            createdAt: blog.createdAt || '',
          };
        });

        mappedBlogs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setBlogs(mappedBlogs.slice(0, 3));
      } catch (error) {
        console.error('Failed to fetch blogs:', error);
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };

    getBlogs();
  }, [assetBaseUrl]);

  return (
    <section className="w-full py-10 md:py-32 bg-[#F8F9FA] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-gradient-to-l from-primary/5 to-transparent pointer-events-none"></div>

      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 xl:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-12 md:mb-20"
        >
          <div className="max-w-2xl">
            {/* <span className="text-orange-main font-bold tracking-widest text-xs uppercase mb-3 block">Knowledge Base</span> */}
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-orange-main mb-4 md:mb-6 tracking-tighter">Read Our Blogs</h2>
            <p className="text-gray-500 text-lg md:text-xl font-medium">Simple advice, up-to-date information, and house planning techniques straight from our top engineers.</p>
          </div>
          <div>
            <Link to="/news-media">
              <button className="bg-transparent border border-orange-main text-title font-semibold px-6 md:px-8 py-2.5 md:py-3 rounded-xl hover:bg-primary hover:text-orange-main transition-colors duration-300 whitespace-nowrap text-sm md:text-base">
                View All Articles
              </button>
            </Link>
          </div>
        </motion.div>

        {/* Mobile Scroller Container */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="flex overflow-x-auto md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 pb-8 md:pb-0 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] -mx-4 px-4 md:mx-0 md:px-0"
        >
          {loading && (
            <div className="w-full text-center text-gray-500 font-medium py-8 md:col-span-2 lg:col-span-3">
              Loading articles...
            </div>
          )}

          {!loading && blogs.length === 0 && (
            <div className="w-full text-center text-gray-500 font-medium py-8 md:col-span-2 lg:col-span-3">
              No articles found.
            </div>
          )}

          {!loading && blogs.map((blog, idx) => (
            <BlogCard key={blog.id || idx} blog={blog} idx={idx} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const BlogCard = ({ blog, idx }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      variants={fadeUp}
      onMouseLeave={() => setIsExpanded(false)}
      className={`w-[85vw] sm:w-[350px] shrink-0 md:w-auto bg-white rounded-[1.5rem] md:rounded-[2rem] overflow-hidden shadow-xl shadow-gray-200/50 group cursor-pointer flex flex-col relative hover:-translate-y-2 transition-transform duration-500 snap-center ${isExpanded ? 'h-auto min-h-[400px] md:min-h-[480px] pb-4' : 'h-[400px] md:h-[480px]'}`}
    >
      {/* Thumbnail Container */}
      <div className={`w-full overflow-hidden relative transition-all duration-700 ease-[0.25,0.1,0.25,1] ${isExpanded ? 'h-[40%]' : 'h-[60%]'}`}>
        {blog.img && (
          <img
            loading="lazy"
            src={blog.img}
            alt={blog.title}
            className="absolute inset-0 w-full h-full object-cover transform scale-110 group-hover:scale-100 transition-transform duration-1000 ease-[0.25,0.1,0.25,1]"
          />
        )}
        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500"></div>
        {/* <div className="absolute top-4 left-4 md:top-6 md:left-6 bg-white/90 backdrop-blur-md px-3 md:px-4 py-1.5 md:py-2 rounded-full text-[10px] md:text-[11px] font-black text-title uppercase tracking-widest z-10 shadow-sm transition-transform duration-500 group-hover:translate-y-[-100%] opacity-100 group-hover:opacity-0">
          {blog.category}
        </div> */}
      </div>

      {/* Content Container */}
      <div className={`p-6 md:p-8 flex flex-col bg-white transition-all duration-700 ease-[0.25,0.1,0.25,1] z-20 rounded-t-[1.5rem] md:rounded-t-[2rem] flex-1 ${isExpanded ? 'relative mt-[-20px]' : 'absolute bottom-0 left-0 right-0 h-[40%] group-hover:h-[65%] md:group-hover:h-[60%]'}`}>
        <h3 className="text-xl md:text-2xl font-bold text-title mb-2 md:mb-3 group-hover:text-orange-main transition-colors leading-tight line-clamp-2 shrink-0">
          {blog.title}
        </h3>
        {/* Description - Slides up on hover */}
        <div className={`overflow-hidden flex-col transition-all duration-700 ease-[0.25,0.1,0.25,1] ${isExpanded ? 'flex flex-1 opacity-100 translate-y-0 relative' : 'flex-grow relative transform translate-y-10 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 delay-100 absolute top-0 w-full'}`}>
          <div className={`${isExpanded ? '' : 'absolute top-0 w-full'}`}>
            <p className={`text-gray-500 text-sm md:text-base leading-relaxed ${isExpanded ? '' : 'line-clamp-3'}`}>
              {blog.desc}
            </p>
            {blog.desc && blog.desc.length > 100 && (
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                className="text-orange-main text-xs font-bold uppercase tracking-widest mt-2 hover:underline self-start"
              >
                {isExpanded ? 'See Less' : 'See More'}
              </button>
            )}
          </div>
        </div>

        <div className={`flex items-center gap-2 shrink-0 ${isExpanded ? 'mt-6' : 'mt-auto'}`}>
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
             <User size={14} className="text-gray-400" />
          </div>
          <span className="text-[11px] font-bold text-gray-600 uppercase tracking-widest">{blog.author}</span>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogSection;
